import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Grid,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { Timestamp } from "firebase/firestore";
import useFirestore from "../../../../config/fireBaseHook";
import { AdminUsersDropdown } from "../../CourseCatalog/DropDowns";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";

const Add = ({ onRecordAdded }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const { addData: addFirestoreData } = useFirestore("ProgramDelivery");

  // Fetch PathwaysPrograms and Sites data for dropdowns
  const { data: pathwaysData } = useFirestore(
    "PathwaysPrograms",
    "PathwayName",
    "asc",
  );
  const { data: sitesData } = useFirestore("Sites", "SiteID", "asc");

  const initialFormData = {
    CCcode: "",
    CoordinatorID: "",
    HighSchool: "",
    PathwayID: "",
    ProgramDeliveryID: "",
    SiteOfDelivery: "",
    SOD_NameForApp: "",
    BegDate: "",
    EndDate: "",
    NonTPconsortium: -1, // Default to "Yes" (-1)
    Notes: "",
    Purge_Ts: "",
    SC_SoD_Inst_Id_Nb: "",
    SiteID: "",
    SODcode: "",
  };

  const [formData, setFormData] = useState(initialFormData);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSiteOfDeliveryChange = (e) => {
    const selectedSiteId = e.target.value;
    const selectedSite = sitesData?.find(
      (site) => site.SiteID === selectedSiteId,
    );

    setFormData((prevData) => ({
      ...prevData,
      SiteOfDelivery:
        selectedSite?.SiteName || selectedSite?.SiteNameShort || "",
      SiteID: selectedSiteId,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: checked,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setSnackbarMessage("");
    try {
      // Prepare data for submission
      const dataToSave = { ...formData };

      // Convert date strings to Firestore Timestamps
      if (dataToSave.BegDate && typeof dataToSave.BegDate === "string") {
        try {
          const begDate = new Date(dataToSave.BegDate);
          if (!isNaN(begDate.getTime())) {
            dataToSave.BegDate = Timestamp.fromDate(begDate);
          } else {
            dataToSave.BegDate = null;
          }
        } catch (error) {
          console.error("Error parsing begin date:", error);
          dataToSave.BegDate = null;
        }
      }

      if (dataToSave.EndDate && typeof dataToSave.EndDate === "string") {
        try {
          const endDate = new Date(dataToSave.EndDate);
          if (!isNaN(endDate.getTime())) {
            dataToSave.EndDate = Timestamp.fromDate(endDate);
          } else {
            dataToSave.EndDate = null;
          }
        } catch (error) {
          console.error("Error parsing end date:", error);
          dataToSave.EndDate = null;
        }
      }

      // Convert numeric fields
      if (dataToSave.MaxEnrollment) {
        dataToSave.MaxEnrollment = parseInt(dataToSave.MaxEnrollment) || 0;
      }
      if (dataToSave.CurrentEnrollment) {
        dataToSave.CurrentEnrollment =
          parseInt(dataToSave.CurrentEnrollment) || 0;
      }

      await addFirestoreData(dataToSave);
      setSnackbarMessage("Program delivery record added successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setFormData(initialFormData);
      setOpen(false);
      if (onRecordAdded) onRecordAdded();
    } catch (error) {
      console.error("Error adding record:", error);
      setSnackbarMessage(`Error adding record: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    if (!loading) {
      setOpen(false);
      setFormData(initialFormData);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        color="success"
        onClick={handleClickOpen}
        startIcon={<FontAwesomeIcon icon={faPlus} />}
      >
        Add Program Delivery
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <ModalDialogTitle onClose={handleClose}>
          Add New Program Delivery Record
        </ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="CC Code"
                name="CCcode"
                value={formData.CCcode}
                onChange={handleChange}
                placeholder="e.g. 001, 002, 003"
                helperText="Include leading zeros (e.g., 001)"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="High School"
                name="HighSchool"
                value={formData.HighSchool}
                onChange={handleChange}
                placeholder="e.g. 001, 002, 003"
                helperText="Include leading zeros (e.g., 001)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <AdminUsersDropdown
                value={formData.CoordinatorID}
                onChange={handleChange}
                name="CoordinatorID"
                label="Coordinator ID"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Pathway ID</InputLabel>
                <Select
                  name="PathwayID"
                  value={formData.PathwayID}
                  onChange={handleChange}
                  label="Pathway ID"
                >
                  <MenuItem value="">
                    <em>Select Pathway</em>
                  </MenuItem>
                  {pathwaysData?.map((pathway) => (
                    <MenuItem
                      key={pathway.id}
                      value={pathway.PathwayID || pathway.id}
                    >
                      {pathway.PathwayName} ({pathway.PathwayID || pathway.id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Program Delivery ID"
                name="ProgramDeliveryID"
                value={formData.ProgramDeliveryID}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Site of Delivery</InputLabel>
                <Select
                  name="SiteOfDelivery"
                  value={formData.SiteID} // Use SiteID as the value for the dropdown
                  onChange={handleSiteOfDeliveryChange}
                  label="Site of Delivery"
                >
                  <MenuItem value="">
                    <em>Select Site</em>
                  </MenuItem>
                  {sitesData
                    ?.slice()
                    .sort((a, b) => {
                      const nameA = (a.SiteName || "").toLowerCase();
                      const nameB = (b.SiteName || "").toLowerCase();
                      return nameA.localeCompare(nameB);
                    })
                    .map((site) => (
                      <MenuItem key={site.id} value={site.SiteID}>
                        {site.SiteName} ({site.SiteID})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SOD Name for App"
                name="SOD_NameForApp"
                value={formData.SOD_NameForApp}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Begin Date"
                name="BegDate"
                type="date"
                value={formData.BegDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date"
                name="EndDate"
                type="date"
                value={formData.EndDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Non-TP Consortium</InputLabel>
                <Select
                  name="NonTPconsortium"
                  value={formData.NonTPconsortium}
                  onChange={handleChange}
                  label="Non-TP Consortium"
                >
                  <MenuItem value={-1}>Yes</MenuItem>
                  <MenuItem value={0}>No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Site ID"
                name="SiteID"
                value={formData.SiteID}
                disabled
                helperText="Auto-populated when Site of Delivery is selected"
                InputProps={{
                  style: { backgroundColor: "#f5f5f5" },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SOD Code"
                name="SODcode"
                value={formData.SODcode}
                onChange={handleChange}
                placeholder="e.g. 001, 002, 003"
                helperText="Include leading zeros (e.g., 001)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SC SoD Inst ID Nb"
                name="SC_SoD_Inst_Id_Nb"
                type="number"
                value={formData.SC_SoD_Inst_Id_Nb}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="Notes"
                value={formData.Notes}
                onChange={handleChange}
                multiline
                rows={4}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions onSave={handleSubmit} saving={loading} />
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Add;
